/**
 * Registro das séries realizadas (A4): lógica pura entre o formulário do atleta e a API.
 * Só séries "tocadas" (editadas, marcadas ou já salvas) são enviadas; o prescrito nunca é alterado.
 */
import type { ExerciseParam, SetResultStatus, StrengthPrescription, StrengthSet, StrengthSetResult } from "../api/types";

export interface LogSet {
  itemKey: string;
  setNo: number;
  touched: boolean;
  status: SetResultStatus;
  reps: string;
  load: string;
  duration: string; // mm:ss
  distance: string;
  rest: string;
  rpe: string;
}

export interface LogItem {
  key: string;
  exerciseId: string | null;
  name: string;
  paramType: ExerciseParam;
  description?: string;
  note?: string;
  sets: { prescribed: StrengthSet; log: LogSet }[];
}

const mmss = (s: number | null | undefined) =>
  s === null || s === undefined ? "" : `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
const txt = (n: number | string | null | undefined) => (n === null || n === undefined ? "" : String(n).replace(".", ","));
const loadTxt = (v: string | null | undefined) => (v === null || v === undefined ? "" : String(Number(v)).replace(".", ","));

export function initLog(strength: StrengthPrescription, saved: StrengthSetResult[]): LogItem[] {
  const by = new Map(saved.map((r) => [`${r.item_key}#${r.set_no}`, r]));
  return strength.items.map((item, idx) => {
    const key = item.key ?? `i${idx + 1}`;
    return {
      key,
      exerciseId: item.exercise_id,
      name: item.name ?? "",
      paramType: item.param_type ?? "reps_load",
      description: item.description,
      note: item.note,
      sets: item.sets.map((p, i) => {
        const r = by.get(`${key}#${i + 1}`);
        const log: LogSet = r
          ? {
              itemKey: key, setNo: i + 1, touched: true, status: r.status, reps: txt(r.reps), load: loadTxt(r.load_kg),
              duration: mmss(r.duration_s), distance: txt(r.distance_m), rest: txt(r.rest_s), rpe: txt(r.rpe),
            }
          : {
              itemKey: key, setNo: i + 1, touched: false, status: "done", reps: txt(p.reps), load: txt(p.load_kg),
              duration: mmss(p.duration_s), distance: txt(p.distance_m), rest: txt(p.rest_s), rpe: "",
            };
        return { prescribed: p, log };
      }),
    };
  });
}

/** "Fiz tudo como prescrito": marca como feitas as séries que ainda não foram registradas. */
export function markAllDone(items: LogItem[]): LogItem[] {
  return items.map((i) => ({ ...i, sets: i.sets.map((s) => (s.log.touched ? s : { ...s, log: { ...s.log, touched: true, status: "done" as const } })) }));
}

const int = (v: string, max: number): number | null => (/^\d{1,6}$/.test(v.trim()) && Number(v) <= max ? Number(v) : null);

function parseDuration(v: string): number | null {
  const tp = v.trim();
  if (!/^\d{1,3}(:\d{1,2}){0,2}$/.test(tp)) return null;
  const p = tp.split(":").map(Number);
  if (p.slice(1).some((x) => x >= 60)) return null;
  const sec = p.length === 1 ? p[0] * 60 : p.length === 2 ? p[0] * 60 + p[1] : p[0] * 3600 + p[1] * 60 + p[2];
  return sec <= 36000 ? sec : null;
}

export type LogErrors = Record<string, string>; // chave `${itemKey}#${setNo}` → código

export function toPayload(items: LogItem[]): { results: Record<string, unknown>[]; errors: LogErrors } {
  const results: Record<string, unknown>[] = [];
  const errors: LogErrors = {};
  for (const item of items) {
    for (const { log } of item.sets) {
      if (!log.touched) continue;
      const id = `${log.itemKey}#${log.setNo}`;
      const out: Record<string, unknown> = { item_key: log.itemKey, set_no: log.setNo, status: log.status };
      if (log.status !== "skipped") {
        if (item.paramType === "reps_load" || item.paramType === "reps") {
          if (log.reps.trim()) {
            const r = int(log.reps, 1000);
            if (r === null) errors[id] = "reps";
            else out.reps = r;
          }
        }
        if (item.paramType === "reps_load" && log.load.trim()) {
          const n = Number(log.load.trim().replace(",", "."));
          if (!/^\d+([.,]\d{1,2})?$/.test(log.load.trim()) || n > 1000) errors[id] = "load";
          else out.load_kg = n;
        }
        if (item.paramType === "time" && log.duration.trim()) {
          const d = parseDuration(log.duration);
          if (d === null) errors[id] = "duration";
          else out.duration_s = d;
        }
        if (item.paramType === "distance" && log.distance.trim()) {
          const d = int(log.distance, 100000);
          if (d === null) errors[id] = "distance";
          else out.distance_m = d;
        }
      }
      if (log.rest.trim()) {
        const r = int(log.rest, 3600);
        if (r === null) errors[id] = "rest";
        else out.rest_s = r;
      }
      if (log.rpe.trim()) {
        const r = int(log.rpe, 10);
        if (r === null || r < 1) errors[id] = "rpe";
        else out.rpe = r;
      }
      results.push(out);
    }
  }
  return { results, errors };
}

export function touchedCount(items: LogItem[]): number {
  return items.reduce((n, i) => n + i.sets.filter((s) => s.log.touched).length, 0);
}
